"use client";

import { useState, useEffect, useId, useMemo, Fragment } from "react";

import { useSelector } from "react-redux";

import PropTypes from "prop-types";
import dynamic from "next/dynamic";
import classnames from "classnames";
import { usePathname, useRouter } from "next/navigation";
import useResponsive from "@/src/utils/useResponsive";

import MockFooter, { bottomPaddingRoutes } from "@/mock/footer";

import { getListingPublishingRules } from "services/modal-content";

import { getToken } from "helpers/auth";

import ModalTextContent from "components/modal-text-content";
import CookiePolicy from "@/modal-contents/cookie-policy";
import GuideContent from "@/modal-contents/guide-content";
import EmployeeCandidateDisclosure from "@/modal-contents/employee-candidate-disclosure";
import UsesRulesContents from "@/modal-contents/uses-rules-contents";
import PrivacyPolicyContent from "@/modal-contents/privacy-policy-content";
import KvkkContent from "@/modal-contents/kvkk-contents";
import ExplicitConsent from "@/modal-contents/explicit-consent";
import { LazyImage } from "src/components/common/lazy-image/LazyImage";

import FooterTabWrapper from "components/footer-tab-wrapper";
import SocialandDownloadLinksMobile from "components/footer-social-downloadLinks";
import ResponsiveContainer from "src/components/grid/responsive-container";

import Divider from "assets/svg/divider.svg";
import EmlakjetLogo from "assets/svg/emlakjetLogo.svg";
import { footerDisabledRoutes } from "@/mock/header";
import ModalHeader from "@/src/components/common/footer/modal-header";

import styles from "./styles.module.css";

const Modal = dynamic(() => import("components/common/modal"), { ssr: false });

const MODAL_GROUP_TITLES = ["Diğer", ""];

const Footer = (props) => {
  const isAuthenticated = useSelector((state) => state.auth.isAuth);

  const id = useId();
  const pathname = usePathname();
  const router = useRouter();
  const { isMobile } = useResponsive();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedModalContent, setSelectedModalContent] = useState(null);
  const [listingPublishingRulesData, setListingPublishingRulesData] = useState(null);
  const [paddingBottom, setPaddingBottom] = useState(0);
  const [authInfo, setAuthInfo] = useState(null);

  const isFooterHidden = footerDisabledRoutes.some((route) => pathname.startsWith(route));
  if (isFooterHidden) return null;

  const socialMediaInfos = [
    { link: "https://www.instagram.com/emlakjet", logo: "/assets/images/footer/instagram.webp", alt: "Instagram" },
    { link: "https://www.facebook.com/Emlakjetcom", logo: "/assets/images/footer/facebook.svg", alt: "Facebook" },
    { link: "https://tr.pinterest.com/emlakjet", logo: "/assets/images/footer/pinterest.webp", alt: "Pinterest" },
    { link: "https://x.com/emlakjet", logo: "/assets/images/footer/x.webp", alt: "X" },
    {
      link: "https://www.linkedin.com/company/emlakjet-com",
      logo: "/assets/images/footer/linkedin.webp",
      alt: "Linkedin",
    },
  ];

  const downloadLinkInfos = [
    {
      link: "https://play.google.com/store/apps/details?id=com.emlakjet.kurumsal.sekizbit",
      logo: "/assets/images/footer/google-play.webp",
      alt: "Playstore",
    },
    {
      link: "https://apps.apple.com/tr/app/emlakjet-emlak-ara-i-lan-ver/id1194656334?l=tr",
      logo: "/assets/images/footer/app-store.webp",
      alt: "Appstore",
    },
    {
      link: "https://appgallery.huawei.com/app/C101850135",
      logo: "/assets/images/footer/app-gallery.webp",
      alt: "Appgallery",
    },
  ];

  const renderContent = (name) => {
    switch (name) {
      case "Çerez Politikası":
        return (
          <ModalTextContent>
            <ModalHeader title="Çerez Politikası" />
            <CookiePolicy />
          </ModalTextContent>
        );
      case "İlan Yayınlama Kuralları":
        return (
          <ModalTextContent>
            <ModalHeader title="İlan Yayınlama Kuralları" />
            <div dangerouslySetInnerHTML={{ __html: listingPublishingRulesData }} />
          </ModalTextContent>
        );
      case "Kullanım Koşulları":
        return (
          <ModalTextContent>
            <ModalHeader title="Kullanım Koşulları" />
            <UsesRulesContents />
          </ModalTextContent>
        );
      case "Üyelik Sözleşmesi ve Gizlilik Politikası":
        return (
          <ModalTextContent>
            <ModalHeader title="Üyelik Sözleşmesi ve Gizlilik Politikası" />
            <PrivacyPolicyContent />
          </ModalTextContent>
        );
      case "İşlem Rehberi":
        return (
          <ModalTextContent>
            <ModalHeader title="İşlem Rehberi" />
            <GuideContent />
          </ModalTextContent>
        );
      case "Kullanıcı Aydınlatma Metni":
        return (
          <ModalTextContent>
            <ModalHeader title="Kullanıcı Aydınlatma Metni" />
            <KvkkContent />
          </ModalTextContent>
        );
      case "Açık Rıza Metni":
        return (
          <ModalTextContent>
            <ModalHeader title="Açık Rıza Metni" />
            <ExplicitConsent />
          </ModalTextContent>
        );
      case "Çalışan Adayı Aydınlatma Metni":
        return (
          <ModalTextContent>
            <ModalHeader title="Çalışan Adayı Aydınlatma Metni" />
            <EmployeeCandidateDisclosure />
          </ModalTextContent>
        );

      default:
        console.error("ERROR!!! Wrong query from modal content!");
        break;
    }
  };

  const modalLinks = useMemo(
    () =>
      (MockFooter.footerListData || [])
        .filter((group) => MODAL_GROUP_TITLES.includes(group.title))
        .flatMap((group) => group.children || []),
    []
  );

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedModalContent(null);
    if (typeof window !== "undefined" && window.location.hash) {
      router.replace(pathname, { scroll: false });
    }
  };

  const handleClick = async (key) => {
    if (key === "İlan Yayınlama Kuralları" && !listingPublishingRulesData) {
      try {
        const res = await getListingPublishingRules();
        setListingPublishingRulesData(res.result);
      } catch (error) {
        console.error(`Error For Listing Publishing Rules!! ${error}`);
      }
    }
    setSelectedModalContent(key);
  };

  const syncFromHash = () => {
    if (typeof window === "undefined") return;

    let slug = window.location.hash.slice(1);
    if (slug.startsWith("/")) slug = slug.slice(1);

    if (!slug) {
      setSelectedModalContent(null);
      setIsModalOpen(false);
      return;
    }

    const link = modalLinks.find((modalLink) => modalLink.slug === slug);
    if (!link || isEfilliSelector(link.name)) {
      setSelectedModalContent(null);
      setIsModalOpen(false);
      return;
    }

    handleClick(link.name);
  };

  const getLinkResult = (child) => {
    if (authInfo && child?.loginUrl) return `${window.env.EJ_APPS_PANEL_HOSTNAME}/${child?.loginUrl}?token=${authInfo}`;
    return child?.href;
  };

  const getCurentYear = () => {
    return new Date().getFullYear();
  };

  const currentYear = getCurentYear();

  useEffect(() => {
    const token = getToken();
    setAuthInfo(token);
  }, [typeof window !== "undefined" && !authInfo, isAuthenticated]);
  const isEfilliSelector = (p) => p === "Çerez Ayarları";

  useEffect(() => {
    if (selectedModalContent !== null) setIsModalOpen(true);
  }, [selectedModalContent]);

  useEffect(() => {
    const hasBottomPadding = bottomPaddingRoutes.includes(pathname) && isMobile;
    if (hasBottomPadding) {
      setPaddingBottom(60);
    }
  }, [pathname, isMobile]);

  useEffect(() => {
    syncFromHash();
    window.addEventListener("hashchange", syncFromHash);
    return () => window.removeEventListener("hashchange", syncFromHash);
  }, []);

  return (
    <div className={styles.wrapper} style={{ paddingBottom }}>
      <ResponsiveContainer>
        <div className={styles.contentWrapper}>
          <div className={styles.logoWrapper}>
            <LazyImage
              src="/assets/icons/logo-footer.svg"
              alt="Emlakjet"
              width={200}
              height={50}
              className={styles.emlakjetLogo}
            />
            <EmlakjetLogo className={styles.mobileEmlakjetLogo} />
          </div>
          <div className={styles.tabWrapper}>
            <FooterTabWrapper />
          </div>
          <div className={styles.line} />
          <div className={styles.listWrapper}>
            {MockFooter.footerListData.map((item, index) => (
              <div key={`${id}+${index}`} className={styles.content}>
                {item?.title && <span className={styles.listTitle}>{item?.title}</span>}
                <div className={styles.listItemsWrapper}>
                  {item?.children.map((child, childIndex) =>
                    item?.title !== "Diğer" && item?.title !== "" ? (
                      <Fragment key={`${id}-${childIndex}`}>
                        <a
                          key={`${id}${childIndex}`}
                          href={getLinkResult(child)}
                          className={styles.category}
                          title={child?.name}
                        >
                          {child?.name}
                        </a>
                        {item?.children?.length - 1 !== childIndex && <Divider className={styles.divider} />}
                      </Fragment>
                    ) : (
                      <Fragment key={`${id}-${childIndex}`}>
                        {isEfilliSelector(child.name) ? (
                          <span
                            className={classnames(styles.category, "efl-edit", styles.categoryButton)}
                            role="button"
                            tabIndex={0}
                            aria-haspopup="dialog"
                            aria-label="Çerez tercihlerini yönet"
                          >
                            {child.name}
                          </span>
                        ) : (
                          <a
                            onClick={() => handleClick(child.name)}
                            className={classnames(styles.category, { _2ejkVv: child.name === "Çerez Politikası" })}
                            href={`#${child.slug}`}
                            title={child?.name}
                          >
                            {child.name}
                          </a>
                        )}
                        {item?.children?.length - 1 !== childIndex && <Divider className={styles.divider} />}
                      </Fragment>
                    )
                  )}
                </div>
              </div>
            ))}
            {isModalOpen && (
              <Modal isOpen={isModalOpen} handleClose={() => handleModalClose()}>
                {renderContent(selectedModalContent)}
              </Modal>
            )}
            <div className={styles.mobileCommunicationWrapper}>
              <p className={styles.listTitle}>İletişim</p>
              <div className={styles.listItemsWrapper}>
                <a href="tel:+908503127373" className={styles.category} title="0850 312 73 73">
                  0850 312 73 73
                </a>
                <Divider className={styles.divider} />
                <a href="/iletisim" target="_blank" className={styles.category} title="Mesaj Gönder">
                  Mesaj Gönder
                </a>
                <Divider className={styles.divider} />
                <a href="/iletisim" target="_blank" className={styles.category} title="İletişim Bilgileri">
                  İletişim Bilgileri
                </a>
              </div>
            </div>
            <SocialandDownloadLinksMobile />
            <div className={styles.communicationInfo}>
              <LazyImage
                src="/assets/icons/communication_logo.svg"
                width={40}
                height={40}
                alt="communication_logo"
                className={styles.communicationLogo}
              />
              <div className={styles.contactUs}>
                <p>Bize Ulaşın</p>
                <span>{`0 (850) 312 73 73`}</span>
                <div className={styles.communicationButtonWrapper}>
                  <a href="/iletisim" className={styles.communicationButton} title="İletişim Bilgileri">
                    İletişim Bilgileri
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.socialWrapper}>
            <div className={styles.mediaLinksWrapper}>
              <div className={styles.mediaTitle}>Bizi Takip Edin</div>
              {socialMediaInfos.map((item, index) => (
                <a
                  className={styles.socialMediaLinks}
                  key={index}
                  target="_blank"
                  href={item.link}
                  rel="nofollow noreferrer"
                  title={item.alt}
                >
                  <LazyImage src={item.logo} width={35} height={35} alt={item.alt} />
                </a>
              ))}
            </div>
            <div className={styles.downloadLinksWrapper}>
              {downloadLinkInfos.map((item, index) => (
                <a key={index} target="_blank" href={item.link} rel="nofollow noreferrer" title={item.alt}>
                  <LazyImage src={item.logo} width={150} height={53} alt={item.alt} />
                </a>
              ))}
            </div>
          </div>
          <div style={{ display: "flex" }}>
            <div className={styles.brandsAndInformationWrapper}>
              <div className={styles.brandsWrapper}>
                <div className={styles.ilab}>
                  <span>Emlakjet bir</span>
                  <a href="https://www.ilab.com.tr/" rel="nofollow noopener noreferrer" target="_blank" title="Ilab">
                    <LazyImage src="/assets/icons/ilab_25_logo.svg" alt="iLab" width={45} />
                  </a>
                  <span>grup şirketidir.</span>
                </div>
                <div className={styles.brands}>
                  {MockFooter.brands.map((brand, index) => (
                    <div key={`${id}-${index}`}>
                      <a
                        href={brand.href}
                        className={styles.brand}
                        target="_blank"
                        rel="nofollow noreferrer"
                        title={brand?.title}
                      >
                        {brand?.title}
                      </a>
                      {index !== MockFooter.brands.length - 1 && <span>|</span>}
                    </div>
                  ))}
                </div>
              </div>
              <span className={styles.information}>
                {`Emlakjet.com © 2006-${currentYear} Emlakjet.com sitesinde kullanıcılar tarafından sağlanan her türlü ilan, bilgi,
            içerik ve görselin gerçekliği, orijinalliği, güvenilirliği ve doğruluğuna ilişkin sorumluluk bu içerikleri
            giren kullanıcıya ait olup, emlakjet.com'un bu hususlarla ilgili herhangi bir sorumluluğu bulunmamaktadır.`}
              </span>
            </div>
          </div>
        </div>
      </ResponsiveContainer>
    </div>
  );
};

Footer.propTypes = {
  props: PropTypes.any,
};

export default Footer;
